import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add, Edit, Delete, DirectionsCar, ArrowForward } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const DistancePricingTab = ({ data, onRefresh, onSuccess, onError }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    fromLocation: '',
    toLocation: '',
    distanceKm: 0,
    transportationType: 'Private Car',
    basePrice: 0,
    pricePerKm: 0,
    isActive: true,
  });

  const transportationTypes = ['Private Car', 'Bus', 'Luxury Car', 'Van', 'Minibus'];

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        fromLocation: item.fromLocation,
        toLocation: item.toLocation,
        distanceKm: item.distanceKm,
        transportationType: item.transportationType,
        basePrice: item.basePrice,
        pricePerKm: item.pricePerKm,
        isActive: item.isActive,
      });
    } else {
      setEditingItem(null);
      setFormData({
        fromLocation: '',
        toLocation: '',
        distanceKm: 0,
        transportationType: 'Private Car',
        basePrice: 0,
        pricePerKm: 0,
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/pricing/distances/${editingItem.id}`,
          formData
        );
        onSuccess('Distance pricing updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pricing/distances`, formData);
        onSuccess('Distance pricing created successfully');
      }
      handleCloseDialog();
      onRefresh();
    } catch (err) {
      onError('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this distance pricing?')) {
      try {
        await axios.delete(`${API_BASE_URL}/pricing/distances/${id}`);
        onSuccess('Distance pricing deleted successfully');
        onRefresh();
      } catch (err) {
        onError('Failed to delete: ' + err.message);
      }
    }
  };

  const calculateTotal = (basePrice, distanceKm, pricePerKm) => {
    return basePrice + (distanceKm * pricePerKm);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Distance & Transport Pricing</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Distance Pricing
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Route</TableCell>
              <TableCell>Distance (km)</TableCell>
              <TableCell>Transportation</TableCell>
              <TableCell>Base Price</TableCell>
              <TableCell>Price/km</TableCell>
              <TableCell>Total Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>{item.fromLocation}</Typography>
                    <ArrowForward fontSize="small" color="action" />
                    <Typography>{item.toLocation}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{item.distanceKm} km</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectionsCar fontSize="small" color="action" />
                    <Typography>{item.transportationType}</Typography>
                  </Box>
                </TableCell>
                <TableCell>${item.basePrice.toFixed(2)}</TableCell>
                <TableCell>${item.pricePerKm.toFixed(2)}</TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="primary">
                    ${calculateTotal(item.basePrice, item.distanceKm, item.pricePerKm).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Distance Pricing' : 'Add Distance Pricing'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="From Location"
              value={formData.fromLocation}
              onChange={(e) =>
                setFormData({ ...formData, fromLocation: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="To Location"
              value={formData.toLocation}
              onChange={(e) =>
                setFormData({ ...formData, toLocation: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Distance (km)"
              type="number"
              value={formData.distanceKm}
              onChange={(e) =>
                setFormData({ ...formData, distanceKm: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.1 }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Transportation Type</InputLabel>
              <Select
                value={formData.transportationType}
                label="Transportation Type"
                onChange={(e) =>
                  setFormData({ ...formData, transportationType: e.target.value })
                }
              >
                {transportationTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Base Price ($)"
              type="number"
              value={formData.basePrice}
              onChange={(e) =>
                setFormData({ ...formData, basePrice: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Price per km ($)"
              type="number"
              value={formData.pricePerKm}
              onChange={(e) =>
                setFormData({ ...formData, pricePerKm: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="body2" color="primary.dark">
                Total Price: ${calculateTotal(formData.basePrice, formData.distanceKm, formData.pricePerKm).toFixed(2)}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DistancePricingTab;
